import React, { forwardRef } from 'react';
import {
  Popover,
  Dialog,
  Slide,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  getPopoverPaperStyle,
  getPopoverHeaderBoxStyle,
  getPopoverHeaderLabelStyle,
  getPopoverHeaderValueStyle,
  getPopoverActionBoxStyle,
  getWithdrawButtonStyle,
  getAddFundsButtonStyle,
  getRefreshIconButtonStyle,
  getOverviewTitleBoxStyle,
  getOverviewTitleStyle,
  getOverviewRowBoxStyle,
  getOverviewRowLabelStyle,
  getOverviewRowValueStyle,
  getMobileBottomSheetPaperStyle,
  getMobileBottomSheetHeaderStyle,
  getMobileBottomSheetActionBoxStyle,
} from './FundsMenuOverlayCss';

const SlideUpTransition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Reusable Funds Menu Overlay.
 * Renders as a Popover on desktop and a Bottom Sheet Dialog on mobile.
 */
const FundsMenuOverlay = ({
  isMobile,
  anchorEl,
  mobileOpen,
  onClose,
  balanceData,
  onWithdrawClick,
  onAddFundsClick,
  onRefreshClick,
}) => {
  const theme = useTheme();

  const overviewItems = [
    { label: 'Balance', value: balanceData?.formatted?.tradingBalance },
    { label: 'Available Margin', value: balanceData?.formatted?.availableCash },
    { label: 'Unrealized P&L', value: balanceData?.formatted?.unrealizedPnl, isPnl: true, positive: balanceData?.isPositive?.unrealizedPnl },
    { label: 'Blocked Margin', value: balanceData?.formatted?.blockedMargin },
    { label: 'Equity', value: balanceData?.formatted?.equity },
    { label: 'Equity Percentage', value: balanceData?.formatted?.equityPercentage },
    { label: 'Credit', value: balanceData?.formatted?.credit, hideIfZero: true },
  ].filter(item => !(item.hideIfZero && parseFloat(item.value?.toString().replace(/[^\d.-]/g, '')) === 0));

  if (!balanceData) return null;

  if (isMobile) {
    return (
      <Dialog
        open={mobileOpen}
        onClose={onClose}
        TransitionComponent={SlideUpTransition}
        PaperProps={{
          sx: getMobileBottomSheetPaperStyle(),
        }}
      >
        {/* Drag Handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: 'divider' }} />
        </Box>

        {/* Header */}
        <Box sx={getMobileBottomSheetHeaderStyle(theme)}>
          <Box>
            <Typography variant="caption" sx={getPopoverHeaderLabelStyle()}>
              Account Value
            </Typography>
            <Typography variant="h6" sx={getPopoverHeaderValueStyle()}>
              {balanceData.formatted?.equity}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Action Buttons */}
        <Box sx={getMobileBottomSheetActionBoxStyle(theme)}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => { onClose(); onWithdrawClick(); }}
            sx={getWithdrawButtonStyle(theme)}
          >
            Withdraw
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => { onClose(); onAddFundsClick(); }}
            sx={getAddFundsButtonStyle(theme)}
          >
            Add Funds
          </Button>
          <IconButton
            size="small"
            onClick={onRefreshClick}
            sx={getRefreshIconButtonStyle(theme)}
          >
            <RefreshIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>

        {/* Overview Section */}
        <Box sx={getOverviewTitleBoxStyle()}>
          <Typography variant="caption" sx={getOverviewTitleStyle()}>
            Overview
          </Typography>
        </Box>
        <Box sx={{ px: 0 }}>
          {overviewItems.map((item, index, arr) => (
            <React.Fragment key={item.label}>
              <Box sx={getOverviewRowBoxStyle()}>
                <Typography sx={getOverviewRowLabelStyle()}>
                  {item.label}
                </Typography>
                <Typography sx={getOverviewRowValueStyle(theme, item)}>
                  {item.value}
                </Typography>
              </Box>
              {index < arr.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
        <Box sx={{ pb: 'calc(env(safe-area-inset-bottom) + 8px)' }} />
      </Dialog>
    );
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        elevation: 8,
        sx: getPopoverPaperStyle(theme),
      }}
    >
      {/* Header */}
      <Box sx={getPopoverHeaderBoxStyle(theme)}>
        <Typography variant="caption" sx={getPopoverHeaderLabelStyle()}>
          Account Value
        </Typography>
        <Typography variant="h6" sx={getPopoverHeaderValueStyle()}>
          {balanceData.formatted?.equity}
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={getPopoverActionBoxStyle(theme)}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AccountBalanceWalletIcon sx={{ fontSize: '0.85rem !important' }} />}
          onClick={() => { onClose(); onWithdrawClick(); }}
          sx={getWithdrawButtonStyle(theme)}
        >
          Withdraw
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: '0.85rem !important' }} />}
          onClick={() => { onClose(); onAddFundsClick(); }}
          sx={getAddFundsButtonStyle(theme)}
        >
          Add Funds
        </Button>
        <IconButton
          size="small"
          onClick={onRefreshClick}
          sx={getRefreshIconButtonStyle(theme)}
        >
          <RefreshIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>

      {/* Overview Section */}
      <Box sx={getOverviewTitleBoxStyle()}>
        <Typography variant="caption" sx={getOverviewTitleStyle()}>
          Overview
        </Typography>
      </Box>
      <Box sx={{ px: 0 }}>
        {overviewItems.map((item, index, arr) => (
          <React.Fragment key={item.label}>
            <Box sx={getOverviewRowBoxStyle()}>
              <Typography sx={getOverviewRowLabelStyle()}>
                {item.label}
              </Typography>
              <Typography sx={getOverviewRowValueStyle(theme, item)}>
                {item.value}
              </Typography>
            </Box>
            {index < arr.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
      <Box sx={{ pb: 1 }} />
    </Popover>
  );
};

export default FundsMenuOverlay;
